const toIsoDate = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const parseIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizePreset = (preset) => ({
  this_week: 'week',
  this_month: 'month',
  this_year: 'year',
}[preset] || preset);

export const getBusinessBoundaryTimes = (businessDayStartHour = 6) => ({
  startTime: `${String(businessDayStartHour).padStart(2, '0')}:00`,
  endTime: `${String((businessDayStartHour + 23) % 24).padStart(2, '0')}:59`,
});

export const hasStandardBusinessBoundary = ({
  startTime,
  endTime,
  businessDayStartHour = 6,
}) => {
  const expected = getBusinessBoundaryTimes(businessDayStartHour);
  return startTime === expected.startTime && endTime === expected.endTime;
};

// Standard ranges are calendar-aligned: weeks start Monday, months on day 1,
// and years on 1 January. The stored end is the final business date; the UI
// displays its 05:59 boundary on the following calendar date.
export const getStandardRangeForAnchor = (preset, anchorDate) => {
  const anchor = parseIsoDate(anchorDate);
  if (!anchor) return null;

  const normalized = normalizePreset(preset);
  let start = new Date(anchor);
  let end = new Date(anchor);

  if (normalized === 'week') {
    const mondayOffset = (anchor.getDay() + 6) % 7;
    start.setDate(anchor.getDate() - mondayOffset);
    end = new Date(start);
    end.setDate(start.getDate() + 6);
  } else if (normalized === 'month') {
    start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  } else if (normalized === 'year') {
    start = new Date(anchor.getFullYear(), 0, 1);
    end = new Date(anchor.getFullYear(), 11, 31);
  } else if (normalized !== 'today') {
    return null;
  }

  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
};

export const resolveStandardStartEdit = ({
  preset,
  nextStartDate,
  startTime,
  endTime,
  businessDayStartHour = 6,
}) => {
  if (normalizePreset(preset) === 'custom' || !hasStandardBusinessBoundary({
    startTime,
    endTime,
    businessDayStartHour,
  })) {
    return null;
  }
  return getStandardRangeForAnchor(preset, nextStartDate);
};

export const shouldKeepStandardPresetForEnd = ({
  preset,
  startDate,
  nextEndDate,
  startTime,
  endTime,
  businessDayStartHour = 6,
}) => {
  if (!hasStandardBusinessBoundary({ startTime, endTime, businessDayStartHour })) return false;
  const expected = getStandardRangeForAnchor(preset, startDate);
  return Boolean(expected && expected.startDate === startDate && expected.endDate === nextEndDate);
};

const shiftDate = (date, unit, amount) => {
  const next = new Date(date);
  if (unit === 'month') {
    next.setMonth(next.getMonth() + amount);
    return next;
  }
  if (unit === 'year') {
    next.setFullYear(next.getFullYear() + amount);
    return next;
  }
  next.setDate(next.getDate() + amount);
  return next;
};

const addMonthsFromPeriodStart = (date, amount) => (
  new Date(date.getFullYear(), date.getMonth() + amount, 1)
);

const addYearsFromPeriodStart = (date, amount) => (
  new Date(date.getFullYear() + amount, 0, 1)
);

const getPresetUnit = (preset) => {
  if (preset === 'week' || preset === 'this_week') return { unit: 'day', amount: 7 };
  if (preset === 'month' || preset === 'this_month') return { unit: 'month', amount: 1 };
  if (preset === 'year' || preset === 'this_year') return { unit: 'year', amount: 1 };
  return { unit: 'day', amount: 1 };
};

export const STANDARD_PRESET_LABELS = {
  today: 'Daily',
  week: 'Weekly',
  this_week: 'Weekly',
  month: 'Monthly',
  this_month: 'Monthly',
  year: 'Annually',
  this_year: 'Annually',
  custom: 'Custom',
  all: 'All Time',
};

export const getStandardPresetLabel = (preset) => STANDARD_PRESET_LABELS[preset] || preset;

export const buildStandardPresetOptions = (values = []) => (
  values.map((value) => ({ value, label: getStandardPresetLabel(value) }))
);

export const shiftDateRangeByPreset = ({ preset, startDate, endDate, delta = 1 }) => {
  const parsedStart = parseIsoDate(startDate);
  const parsedEnd = parseIsoDate(endDate);
  if (!parsedStart || !parsedEnd || !delta) {
    return { startDate, endDate };
  }

  if (preset === 'custom') {
    const daySpan = Math.max(1, Math.round((parsedEnd - parsedStart) / 86400000) + 1);
    const amount = daySpan * delta;
    return {
      startDate: toIsoDate(shiftDate(parsedStart, 'day', amount)),
      endDate: toIsoDate(shiftDate(parsedEnd, 'day', amount)),
    };
  }

  if (preset === 'all') {
    return { startDate, endDate };
  }

  if (preset === 'month' || preset === 'this_month') {
    const nextStart = addMonthsFromPeriodStart(parsedStart, delta);
    const nextEnd = new Date(nextStart.getFullYear(), nextStart.getMonth() + 1, 0);
    return {
      startDate: toIsoDate(nextStart),
      endDate: toIsoDate(nextEnd),
    };
  }

  if (preset === 'year' || preset === 'this_year') {
    const nextStart = addYearsFromPeriodStart(parsedStart, delta);
    const nextEnd = new Date(nextStart.getFullYear(), 11, 31);
    return {
      startDate: toIsoDate(nextStart),
      endDate: toIsoDate(nextEnd),
    };
  }

  const { unit, amount } = getPresetUnit(preset);
  const shiftAmount = amount * delta;
  return {
    startDate: toIsoDate(shiftDate(parsedStart, unit, shiftAmount)),
    endDate: toIsoDate(shiftDate(parsedEnd, unit, shiftAmount)),
  };
};
